import { CharacterCard, CharacterCardActions, CharacterCardVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { CHARACTER_MODERATION_STATUS, useTranslation } from "@next-platform/client-common-core";
import { Spinner } from "@nextui-org/react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import NavigateBackButton from "@/components/common/NavigateBackButton";
import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag, useGroupChatDrafts } from "@/hooks";
import { AvatarPreset, IDraftGroupCharacter } from "@/types";
import { getAvatarUrl, getCharacterCardStatusBadgeLabel, getCharacterCardVariantLabel } from "@/utils";

export default function GroupChatDraftPage() {
  const navigate = useNavigate();
  const { groupChatDrafts, isLoading, deleteDraft, reloadDrafts } = useGroupChatDrafts();
  const { t } = useTranslation();
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);

  useEffect(() => {
    reloadDrafts();
  }, [reloadDrafts]);

  const handleDeleteDraft = (draftId: string) => {
    deleteDraft(draftId);
    if (groupChatDrafts.length === 1) {
      navigate(ROUTES.chatbots);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!groupChatDrafts || groupChatDrafts.length === 0) {
    return (
      <>
        <NavigateBackButton />
        <div className="flex flex-col items-center justify-center h-full">
          <Text variant={TextVariants.HEADING_5_MEDIUM}>
            <Trans i18nKey="myCreation:page.noGroupChatDrafts" />
          </Text>
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="mt-2">
            <Trans i18nKey="myCreation:page.noGroupChatDraftsDescription" />
          </Text>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigateBackButton />
      <Text variant={TextVariants.HEADING_5_MEDIUM}>
        <Trans i18nKey="myCreation:page.yourGroupChatDrafts" />
      </Text>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 mt-lg">
        {groupChatDrafts.map((draft: IDraftGroupCharacter, idx: number) => {
          const draftId = draft?.id || draft?.character_id;
          const moderationStatus = draft?.moderationReport?.moderation_status;
          const variant =
            moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED ? CharacterCardVariant.DRAFT_ACTION_REQUIRED : CharacterCardVariant.DRAFT;

          const variantLabel = getCharacterCardVariantLabel(variant, t);
          const statusBadgeLabel = getCharacterCardStatusBadgeLabel(variant, t);

          return (
            <CharacterCard
              key={draftId || idx}
              variant={variant}
              characterInfo={{
                characterId: draftId || "",
                characterName: draft?.name || t("myCreation:card.untitledGroup"),
                characterCreatorUserName: draft?.creator_username || "",
                characterTitle: draft?.title || "",
                characterMessageNumber: "-",
                characterTokensCount: "-",
                characterAvatarUrl:
                  getAvatarUrl(draft?.avatar_url || "", {
                    preset: AvatarPreset.AVATAR_256X256,
                  }) || "",
                characterTags: draft?.tags,
                hasVoice: hasVoiceAttachmentFeatureFlag && !!draft?.voice_id,
                creatorPagePath: `/creator/${draft?.creator_username}`,
                chatPagePath: `/group/create/${draftId}`,
                characterInfoLabel: t("myCreation:card.groupDraft"),
                lorebookAttached: !!draft?.has_lorebooks,
                lorebookLabel: draft?.has_lorebooks ? t("chatbotCard:tooltip.lorebookAttached") : undefined,
                variantLabel,
                statusBadgeLabel,
              }}
              characterCardActionsState={{
                [CharacterCardActions.FAVORITE]: {
                  isHidden: true,
                },
                [CharacterCardActions.INFO]: {
                  isHidden: true,
                },
              }}
              dropdownMenuActions={[
                {
                  icon: IconVariants.TRASH,
                  label: t("myCreation:card.actions.remove"),
                  onPress: () => handleDeleteDraft(draftId || ""),
                  className: "text-red-9",
                },
              ]}
            />
          );
        })}
      </div>
    </>
  );
}
